import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useAuth } from "@clerk/react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { getGetCurrentUserQueryKey } from "@workspace/api-client-react";
import {
  ENDPOINT,
  STILL_SETTING_UP,
  clearPendingAction,
  loadPending,
  startFreeSignup,
} from "@/lib/freeSignup";

// #81 F2 (web) — where the Free journey returns. The webhook that attaches the new subscription can
// lag the redirect, so this page waits for /api/me.hasSubscriptionInForce — the Free gate's OWN
// condition, not hasSubscription (any row, including an abandoned pending_checkout) — then replays the
// saved first action once and lands the runner where it leads.
//
// Bounded (Celeste's !139 review): polling backs off 3s → 6s → 12s → 24s → 60s, offers Try again at
// 90 seconds, and stops entirely at 15 minutes, so a tab left open overnight does not run up tens of
// thousands of requests. The saved action is NEVER discarded on a transient answer (429 rate limit,
// 503, or the gate's 402 again) — only a genuine refusal clears it.
//
// Kayla's ruling (2026-10-30, Gerald: "Contact support is in no way even a consideration"): a runner
// stuck here is a PRODUCT failure, so the product recovers on its own. Try again restarts the Free
// hand-off — back into the same journey with the pending action kept — and stays available after
// polling stops (including !152's two-hour window). Reaching it sends one Sentry warning, so WE see
// the stuck runner without them having to tell us. No support link.
export const BACKOFF_MS = [3_000, 6_000, 12_000, 24_000, 60_000];
export const RETRY_AFTER_MS = 90_000;
export const STOP_AFTER_MS = 15 * 60_000;
// Emily (CMO), !139 copy decision 2026-10-30: says the join/claim is kept, so nobody starts over.
export const RETRY_LINE = "We’ll keep what you were doing while you try setup again.";
export const RETRY_LABEL = "Try again";

type State = { phase: "waiting" } | { phase: "error"; message: string };
type Timing = { backoffMs: number[]; retryAfterMs: number; stopAfterMs: number };

const TRANSIENT = new Set([402, 429, 503]);

export default function FinishingSetup({
  backoffMs = BACKOFF_MS,
  retryAfterMs = RETRY_AFTER_MS,
  stopAfterMs = STOP_AFTER_MS,
}: Partial<Timing>) {
  const { getToken, userId } = useAuth();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [state, setState] = useState<State>({ phase: "waiting" });
  const [offerRetry, setOfferRetry] = useState(false);
  const started = useRef(Date.now());
  // Timing and the one-time report live in refs: a re-render (e.g. Try again appearing) must
  // never restart the loop or report twice.
  const timing = useRef<Timing>({ backoffMs, retryAfterMs, stopAfterMs });
  const reported = useRef(false);

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let tries = 0;
    const pending = loadPending(userId)?.action ?? null;
    const { backoffMs: backoff, retryAfterMs: retryAfter, stopAfterMs: stopAfter } = timing.current;

    const again = () => {
      const elapsed = Date.now() - started.current;
      if (elapsed >= retryAfter) {
        setOfferRetry(true);
        if (!reported.current) {
          reported.current = true;
          Sentry.captureMessage("Free sign-up return: subscription not in force after the retry threshold", {
            level: "warning",
            tags: { action: pending?.kind ?? "none" },
            fingerprint: ["free-signup-return-stuck"],
          });
        }
      }
      if (elapsed >= stopAfter) return; // bounded: the runner has Try again by now
      const delay = backoff[Math.min(tries, backoff.length - 1)]!;
      tries += 1;
      timer = setTimeout(attempt, delay);
    };

    const attempt = async () => {
      if (stopped) return;
      if (!pending) {
        // Nothing to resume (opened directly, or storage unavailable): back to setup.
        setLocation("/dashboard");
        return;
      }
      try {
        const token = await getToken();
        const auth = { Authorization: `Bearer ${token}` };
        const me = await fetch("/api/me", { headers: auth }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
        if (!me?.hasSubscriptionInForce) {
          again();
          return;
        }
        const res = await fetch(ENDPOINT[pending.kind], {
          method: "POST",
          headers: { "Content-Type": "application/json", ...auth },
          body: JSON.stringify(pending.body),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          clearPendingAction(userId);
          await queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
          setLocation("/dashboard");
          return;
        }
        if (TRANSIENT.has(res.status)) {
          again(); // keep the saved action; try again later within the bounds
          return;
        }
        if (data?.code === "region_unavailable") {
          clearPendingAction(userId);
          setLocation("/region");
          return;
        }
        clearPendingAction(userId);
        setState({ phase: "error", message: typeof data?.error === "string" ? data.error : STILL_SETTING_UP });
      } catch {
        again();
      }
    };
    void attempt();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [getToken, userId, setLocation, queryClient]);

  // Back into the same Free journey with the pending action kept. startFreeSignup re-saves it, so each
  // Try again restarts the two-hour window: a runner actively retrying is present and should not
  // expire (a deliberate choice, Celeste's !139 note).
  //
  // With no saved journey (a record from an older build, or storage unavailable) the action cannot be
  // replayed, so it is CLEARED first: otherwise !152's guard would see the pending action on
  // /dashboard and bounce straight back here, and Try again would do nothing (Celeste). Cleared, the
  // runner reaches setup and starts the hand-off afresh — never a dead end.
  function retry() {
    const saved = loadPending(userId);
    if (saved && userId) {
      startFreeSignup(userId, saved.handoffUrl, saved.action);
      return;
    }
    clearPendingAction(userId);
    setLocation("/dashboard");
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md text-center" role="status" aria-live="polite">
        <span className="font-display text-2xl font-bold tracking-tight">
          PEAK<span className="text-primary">RUNNING</span>
        </span>
        <p className="mt-6 text-sm text-foreground" data-testid="setting-up-message">
          {state.phase === "error" ? state.message : STILL_SETTING_UP}
        </p>
        {offerRetry && state.phase === "waiting" && (
          <div className="mt-4" data-testid="setting-up-retry">
            <p className="text-sm text-muted-foreground mb-3">{RETRY_LINE}</p>
            <button
              type="button"
              onClick={retry}
              className="bg-primary text-primary-foreground rounded px-4 py-2 text-sm font-semibold hover:opacity-90"
            >
              {RETRY_LABEL}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
